import { Guid } from 'guid-typescript';
import lodash from 'lodash';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch, useSelector } from 'react-redux';

import { cacheApi, recaptchaAPI } from '@/main/api';
import { SpinnerLoading } from '@/main/components/shared/styles';
import { useTranslateHook } from '@/main/hooks/translateHook';
import { KeyValue } from '@/main/models/common';
import {
  getStringStorage,
  LASTSYNCTRANSLATIONS
} from '@/main/services/storage';
import {
  getCultureBrowser,
  getTranslateByKey,
  handleGeneralError,
  isEmpty,
  isNullUndefined
} from '@/main/services/utils';
import { saveTranslates } from '@/main/store/translate';

import { CustomError } from '@/main/services/class/customError';
import { AxiosError } from 'axios';
import { ContactStepModel } from './steps/contact/utilsContact';
import { DistributionRegionStepModel } from './steps/distributionRegion/utilsDistributionRegions';
import { InsurersStepModel } from './steps/insurers/utilsInsurers';
import { LegalInformationForm } from './steps/legalInformation/context';
import { SupplyConfigurationStepModel } from './steps/supplyConfiguration/utilsSupplyConfiguration';
import { WarehouseForm } from './steps/warehouseData/context';
import { TypeRegisterStep, TypeRegisterStepScreen } from './types';
import { ENUM_ERROR_EMAIL, handleAxiosError, handleValidationError } from './utilsSteps';

const DATA_INITIAL_FORM_STATE_ROOT = {
  companyData: {
    guidCountry: '',
    isoCodeCountry: '',
    prefixPhone: '',
    nameCountry: '',
    firstName: '',
    lastName: '',
    email: '',
    companyName: ''
  },
  legalInformation: {} as LegalInformationForm,
  contacts: [] as ContactStepModel[],
  address: {
    addressType: null as KeyValue | null,
    address: '',
    city: null as KeyValue | null,
    state: null as KeyValue | null,
    complement: '',
    number: '',
    zipCode: '',
    noNumber: false,
    municipality: null as KeyValue | null
  },
  warehouse: {} as WarehouseForm,
  distributionRegion: [] as DistributionRegionStepModel[],
  supplyConfiguration: [] as SupplyConfigurationStepModel[],
  insurers: [] as InsurersStepModel[],
  summary: {}
};
const INITIAL_FORM_STATE_ROOT = {
  typeRegister: '' as TypeRegisterStepScreen,
  idClient: '',
  step: '' as TypeRegisterStep,
  userEmail: '',
  dateAndHour: '', // Notation: Backend send/save this information
  data: DATA_INITIAL_FORM_STATE_ROOT,
  oldEmail: '', // Notation: This field is used to update the email in the cache, when the user changes the email
  infoCountry: {
    guidCountry: '',
    isoCodeCountry: '',
    prefixPhoneDefault: '',
    name: ''
  }
};
type TypeFormRootStep = typeof INITIAL_FORM_STATE_ROOT;
export type TypeDataFormRootStep = typeof DATA_INITIAL_FORM_STATE_ROOT;
type TypeEdit = '' | 'default' | 'summary' | 'save';
type TypeInfoEdit<T> = {
  type: TypeEdit;
  info: T | null;
};
interface RegisterStepsContextData {
  currentStep: TypeRegisterStep;
  availableSteps: TypeRegisterStep[];
  formRootStep: TypeFormRootStep;
  completedSteps: TypeRegisterStep[];
  isEdit: boolean;
  isLoadingSaveStep: boolean;
  isLoadingFormRootStep: boolean;
  typeRegisterScreen: TypeRegisterStepScreen;
  isDirtyForm: boolean;
  setIsDirtyForm: (isDirt: boolean) => void;
  getInfoEdit: <T>() => TypeInfoEdit<T>;
  updateFormRootStep: (
    step: TypeRegisterStep,
    data: Partial<TypeDataFormRootStep>
  ) => Promise<void>;
  loadFormRootStep: (email: string) => Promise<boolean>;
  navigateToStep: <T>(
    step: TypeRegisterStep,
    origin?: TypeEdit,
    infoEdit?: T
  ) => void;
  navigateToLastStep: () => void;
  nextStep: () => void;
  previousStep: () => void;
}

export const RegisterStepsContext = createContext<RegisterStepsContextData>(
  {} as RegisterStepsContextData
);

/**
 * Custom hook to use the RegisterStepsContext
 * This hook is used to access the context of the register steps
 *
 * @param currentStep         - Current step of the register
 * @param availableSteps      - Steps available to render
 * @param formRootStep        - Object with the form data of the root step
 * @param completedSteps      - Steps that have already been completed
 * @param isEdit              - If true, the current step is in edit mode
 * @param isLoadingSaveStep   - If true, the step is being saved
 * @param updateFormRootStep  - Function to update the form data of the root step
 * @param loadFormRootStep    - Function to load the form data of the root step
 * @param navigateToStep      - Function to navigate to a specific step and complete the previous steps
 * @param nextStep            - Function to navigate to the next step
 * @param previousStep        - Function to navigate to the previous step
 * @param getInfoEdit         - Function to get the information of the edit
 * @param navigateToLastStep  - Function to navigate to the last step
 * @param isLoadingFormRootStep - If true, the form root step is being loaded
 * @param typeRegisterScreen  - Type of the register screen
 *
 * @returns {RegisterStepsContextData} - Context of the register steps
 * @example
 * const {
 *  currentStep,
 * availableSteps,
 * formRootStep,
 * completedSteps,
 * isEdit,
 * isLoadingSaveStep,
 * updateFormRootStep,
 * loadFormRootStep,
 *  navigateToStep,
 * nextStep,
 * previousStep,
 * } = useRegisterSteps();
 *
 *
 */
export const useRegisterSteps = () => useContext(RegisterStepsContext);
interface IRegisterStepsProviderProps {
  children: React.ReactNode;
  orderStepsToRender: TypeRegisterStep[];
  typeRegister: TypeRegisterStepScreen;
}
export const RegisterStepsProvider = ({
  children,
  orderStepsToRender,
  typeRegister
}: IRegisterStepsProviderProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const dispatch = useDispatch();
  const translatesRedux = useSelector((state: any) => state.translate); //Notation: not removed this line (used of re-render when load translates)
  const {
    isLoadingTranslates,
    translatesLocal: translatesLocalHook,
    getTranslates
  } = useTranslateHook({
    fillTranslateLocal: false
  });

  const [currentStep, setCurrentStep] = useState<TypeRegisterStep>(
    'CompanyData'
  );
  const [availableSteps, setStepsRender] = useState<TypeRegisterStep[]>([]); // Notation: Steps to render
  const [completedSteps, setCompletedSteps] = useState<TypeRegisterStep[]>([]);
  const [formRootStep, setFormRootStep] = useState<TypeFormRootStep>(
    INITIAL_FORM_STATE_ROOT
  );
  const [isLoadingSaveStep, setIsLoadingSaveStep] = useState<boolean>(false);
  const [isDirtyForm, setIsDirtyFormState] = useState<boolean>(false);
  const [isLoadingFormRootStep, setIsLoadingFormRootStep] = useState<boolean>(
    false
  );
  const [infoEdit, setInfoEdit] = useState<any | null>(null);
  const isEdit = completedSteps.includes(currentStep);

  //#region private functions
  function clearCompleteSteps() {
    setCompletedSteps([]);
  }
  function completeStep(step: TypeRegisterStep, completeCurrentStep = true) {
    const hasCompleted = completedSteps.includes(step);
    if (hasCompleted) return;

    const previousStepsUnclompleted = availableSteps.filter(
      step => !completedSteps.includes(step)
    );
    const indexStep = previousStepsUnclompleted.indexOf(step);
    const previousSteps = previousStepsUnclompleted.slice(0, indexStep);
    setCompletedSteps([...completedSteps, ...previousSteps, ...(completeCurrentStep ? [step] : [])]);
  }

  async function validateRecaptcha() {
    if (!executeRecaptcha) return;
    const token = await executeRecaptcha();
    const response = await recaptchaAPI.validateRecaptcha(token);
    const {
      data: { result }
    } = response;
    const isValidCaptcha = result;

    if (!isValidCaptcha) {
      const message = getTranslateByKey("information.failReCaptcha", true);
      throw new CustomError(message, ENUM_ERROR_EMAIL.CAPTCHA)
    }

    return isValidCaptcha;
  }

  async function loadFormRootStep(email: string) {
    try {
      setIsLoadingFormRootStep(true);
      const isValidCaptcha = await validateRecaptcha();
      if (!isValidCaptcha) return false;

      const response = await cacheApi.get(email);
      const {
        data: { result }
      } = response;

      if (!result) return false;

      const data: TypeFormRootStep = JSON.parse(result);
      const isOtherTypeRegister =
        !isNullUndefined(data.typeRegister) &&
        data.typeRegister !== typeRegister;
      if (isOtherTypeRegister) {
        const message = getTranslateByKey('information.emailFillEdAnotherRegisterPublic', true)
        throw new CustomError(message, ENUM_ERROR_EMAIL.EMAIL)
      }
      const updatedGuidCountry = !isEmpty(data.data.companyData.guidCountry)
        ? data.data.companyData.guidCountry
        : '';
      const updatedData: TypeFormRootStep = {
        ...data,
        infoCountry: {
          ...data.infoCountry,
          guidCountry: updatedGuidCountry
        }
      };
      clearCompleteSteps();
      completeStep(updatedData.step, false);
      setFormRootStep(updatedData);
      return true;
    } catch (error) {
      if (error instanceof AxiosError) { handleAxiosError(error) }
      else if (error instanceof CustomError) { handleValidationError(error) }
      else { handleGeneralError() }
      return false;
    } finally {
      setIsLoadingFormRootStep(false);
    }
  }
  async function loadTranslates() {
    const cultureBrowser = getCultureBrowser();
    const lastSyncTranslations = getStringStorage(
      `${LASTSYNCTRANSLATIONS}_${cultureBrowser}`
    );
    const responseApi = await getTranslates({
      culture: cultureBrowser,
      lastSyncTranslations
    });
    if (responseApi.data.result) {
      // Save translates
      dispatch(
        saveTranslates(responseApi.data.result, cultureBrowser, false, [])
      );
    }
  }
  function clearDataEdit() {
    setInfoEdit(null);
  }

  //#endregion

  //#region public functions
  function setIsDirtyForm(value: boolean) {
    setIsDirtyFormState(value)
  };
  function nextStep() {
    const indexCurrentStep = availableSteps.indexOf(currentStep);
    const nextStep = availableSteps[indexCurrentStep + 1];

    if (!nextStep) return;

    clearDataEdit();
    completeStep(currentStep);
    setCurrentStep(nextStep);
  }
  function navigateToLastStep() {
    const indexLastStepActive = availableSteps.indexOf(
      completedSteps[completedSteps.length - 1]
    );
    const lastStep = availableSteps[indexLastStepActive + 1];
    setCurrentStep(lastStep);
  }
  function previousStep() {
    const indexCurrentStep = availableSteps.indexOf(currentStep);
    const previousStep = availableSteps[indexCurrentStep - 1];
    if (!previousStep) return;
    setCurrentStep(previousStep);
  }
  function navigateToStep<T>(
    step: TypeRegisterStep,
    origin: TypeEdit = 'default',
    infoEdit: T | null = null
  ) {
    const indexNavigateStep = availableSteps.indexOf(step);
    const isFirstIndex = indexNavigateStep === 0;

    if (!isFirstIndex) {
      const previousStep = availableSteps[indexNavigateStep - 1];
      completeStep(previousStep);
    }
    setInfoEdit({
      type: origin === 'save' ? 'default' : origin,
      info: isNullUndefined(infoEdit) ? {} : infoEdit
    });
    setCurrentStep(step);
    if (!isEdit && origin === 'save') {
      saveStepInFormRootStep(step);
    }
  }
  async function saveStepInFormRootStep(step: TypeRegisterStep) {
    try {
      setIsLoadingSaveStep(true);
      const cloneFormRootStep = lodash.cloneDeep(formRootStep);
      const updatedFormRootStep: TypeFormRootStep = {
        ...cloneFormRootStep,
        step
      }

      const JSONtoString = JSON.stringify(updatedFormRootStep);
      const response = await cacheApi.save(cloneFormRootStep.userEmail, JSONtoString);

      setFormRootStep(updatedFormRootStep);
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingSaveStep(false);

    }

  }
  async function updateFormRootStep(
    step: TypeRegisterStep,
    data: Partial<TypeDataFormRootStep>
  ) {
    setIsLoadingSaveStep(true);
    try {
      const isValidCaptcha = await validateRecaptcha();
      if (!isValidCaptcha) throw new Error('Invalid captcha');
      const cloneFormRootStep = lodash.cloneDeep(formRootStep);

      const RANGE_STEP = isEdit ? 1 : 2;
      const isLastStep = availableSteps.indexOf(step) === availableSteps.length - 2;
      const indexLastActive = isLastStep
        ? availableSteps.indexOf(step)
        : availableSteps.indexOf(completedSteps[completedSteps.length - 1]) + RANGE_STEP;
      const stepToSave = availableSteps[indexLastActive];

      const userEmail = !isNullUndefined(data.companyData)
        ? data.companyData.email
        : formRootStep.userEmail;
      const isChangeEmail = userEmail !== formRootStep.userEmail;

      const newFormRootStep: TypeFormRootStep = {
        ...cloneFormRootStep,
        typeRegister: typeRegister,
        userEmail,
        idClient: isEmpty(formRootStep.idClient) ? String(Guid.create()) : formRootStep.idClient,
        step: stepToSave,
        ...(isChangeEmail && { oldEmail: formRootStep.userEmail }),
        infoCountry: {
          ...cloneFormRootStep.infoCountry,
          guidCountry: !isNullUndefined(data.companyData)
            ? data.companyData.guidCountry
            : formRootStep.infoCountry.guidCountry,
          isoCodeCountry: !isNullUndefined(data.companyData)
            ? data.companyData.isoCodeCountry
            : formRootStep.infoCountry.isoCodeCountry,
          prefixPhoneDefault: !isNullUndefined(data.companyData)
            ? data.companyData.prefixPhone
            : formRootStep.infoCountry.prefixPhoneDefault,
          name: !isNullUndefined(data.companyData)
            ? data.companyData.nameCountry
            : formRootStep.infoCountry.name
        },
        data: {
          ...cloneFormRootStep.data,
          ...data
        }
      };

      const JSONtoString = JSON.stringify(newFormRootStep);
      const response = await cacheApi.save(userEmail, JSONtoString);

      setFormRootStep(newFormRootStep);
    } catch (error) {
    } finally {
      setIsLoadingSaveStep(false);
    }
  }

  function getInfoEdit<T>(): TypeInfoEdit<T> {
    const formattedInfoEdit = isNullUndefined(infoEdit)
      ? { type: '', info: null }
      : infoEdit;
    return formattedInfoEdit as TypeInfoEdit<T>;
  }
  //#endregion

  useEffect(() => {
    loadTranslates();
  }, []);

  useEffect(() => {
    const firstArray = orderStepsToRender[0];
    setCurrentStep(firstArray);
    setStepsRender(orderStepsToRender);
  }, [JSON.stringify(orderStepsToRender)]);

  useEffect(() => {
    setIsDirtyForm(false);
  }, [currentStep]);

  if (isLoadingTranslates) return <SpinnerLoading />;
  if (!availableSteps.length) return null;

  return (
    <RegisterStepsContext.Provider
      value={{
        currentStep,
        availableSteps,
        formRootStep,
        completedSteps,
        isEdit,
        isLoadingSaveStep,
        isLoadingFormRootStep,
        typeRegisterScreen: typeRegister,
        isDirtyForm,
        setIsDirtyForm,
        navigateToLastStep,
        getInfoEdit,
        loadFormRootStep,
        updateFormRootStep,
        navigateToStep,
        nextStep,
        previousStep
      }}>
      {children}
    </RegisterStepsContext.Provider>
  );
};

export default RegisterStepsProvider;
